"use client";

import { createContext, useContext, useState, useCallback } from "react";
import type { HeaderContextProps } from "./types/header.types";
import { useKeypress } from "../../hooks";

/**
 * The id the mobile menu carries and its toggle points `aria-controls` at.
 *
 * A fixed string rather than `useId`: the id has to be identical in the server's HTML and in the
 * client's first render, and `useId` derives its value from the component's position in the React
 * tree — which is not the same tree on both sides in every framework that renders MDS. Gatsby is
 * the case in hand: its browser runtime wraps the page in more providers than its SSR pass does, so
 * the two renders produce different ids and React reports a hydration mismatch on this attribute.
 *
 * One `Header` per page is the assumption this rests on, and the component already makes it: the
 * menu is a fixed overlay that locks body scroll while it is open, so a second one on the same page
 * could not work anyway. `menuId` on `Header` is there for anyone who has to prove otherwise.
 */
const DEFAULT_MENU_ID = "mds-header-mobile-menu";

// eslint-disable-next-line react-refresh/only-export-components -- exports context alongside provider
export const HeaderContext = createContext<HeaderContextProps>({
  menuId: "",
  menuOpen: false,
  setMenuOpen: () => {},
  toggleMenu: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components -- exports context alongside provider
export const useHeaderContext = () => useContext(HeaderContext);

interface HeaderProviderProps {
  children: React.ReactNode;
  onMenuOpenChange?: (open: boolean) => void;
  menuId?: string;
}

export const HeaderProvider: React.FC<HeaderProviderProps> = ({
  children,
  onMenuOpenChange,
  menuId = DEFAULT_MENU_ID,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => {
      const next = !prev;
      onMenuOpenChange?.(next);
      return next;
    });
  }, [onMenuOpenChange]);

  const handleSetMenuOpen = useCallback(
    (open: boolean) => {
      onMenuOpenChange?.(open);
      setMenuOpen(open);
    },
    [onMenuOpenChange],
  );

  useKeypress("Escape", () => {
    handleSetMenuOpen(false);
  });

  return (
    <HeaderContext.Provider value={{ menuId, menuOpen, setMenuOpen: handleSetMenuOpen, toggleMenu }}>
      {children}
    </HeaderContext.Provider>
  );
};
